import { cataloguesApi } from "@/services/dashboard-api.client";
import type { IngestJob } from "@sakura-tech-lab/dashboard-api";

// The generated wire model: camelCase, `Date` objects after client parsing.
// `status` stays a plain string — the dashboard API relays the product-ingest
// payload verbatim and deliberately keeps the status open-ended.
export type { IngestJob };

export type IngestJobStatus = "pending" | "processing" | "completed" | "failed";

export const TERMINAL_STATUSES: ReadonlySet<string> = new Set<IngestJobStatus>([
  "completed",
  "failed",
]);
export const ingestJobsCacheKey = (catalogueId: string) =>
  ["catalogue-ingest-jobs", catalogueId] as const;
export const ingestJobCacheKey = (catalogueId: string, jobId: string) =>
  ["catalogue-ingest-job", catalogueId, jobId] as const;

export function isJobActive(job: IngestJob): boolean {
  return !TERMINAL_STATUSES.has(job.status);
}

export const IngestJobsService = {
  async list(catalogueId: string): Promise<IngestJob[]> {
    try {
      const response = await cataloguesApi.listCatalogueIngestJobs({ id: catalogueId });
      const jobs = response.data?.jobs;
      return Array.isArray(jobs) ? jobs : [];
    } catch {
      // Older deployments and unavailable catalogues degrade to no banner.
      return [];
    }
  },

  async get(catalogueId: string, jobId: string): Promise<IngestJob | null> {
    try {
      const response = await cataloguesApi.getCatalogueIngestJob({ id: catalogueId, jobId });
      return response.data ?? null;
    } catch {
      return null;
    }
  },
};

export type IngestJobTone = "info" | "success" | "warning" | "error";

export interface IngestJobDisplay {
  tone: IngestJobTone;
  label: string;
  detail?: string;
  /** 0-100 for a determinate bar, or null for an indeterminate (spinner) state. */
  percent: number | null;
}

/** Single source of truth for how a job renders, shared by the banner and Tasks page. */
export function describeIngestJob(job: IngestJob): IngestJobDisplay {
  const total = job.totalProducts;
  const processed = job.processedProducts;
  const skipped = job.skippedCount ?? 0;

  if (job.status === "failed") {
    return { tone: "error", label: "Import failed", detail: job.error || job.lastError || undefined, percent: 100 };
  }

  if (job.status === "completed") {
    const imported = processed ?? total ?? 0;
    if (skipped > 0) {
      return {
        tone: "warning",
        label: "Import finished with skips",
        detail: `${imported.toLocaleString()} imported, ${skipped.toLocaleString()} skipped`,
        percent: 100,
      };
    }
    return { tone: "success", label: "Import complete", detail: `${imported.toLocaleString()} items imported`, percent: 100 };
  }

  if (job.status === "pending") {
    return { tone: "info", label: "Import queued", detail: "Waiting for a worker to pick it up…", percent: null };
  }

  // processing
  if (total != null && processed != null) {
    const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
    const remaining = Math.max(total - processed, 0);
    return {
      tone: "info",
      label: "Importing products",
      detail: `${processed.toLocaleString()} of ${total.toLocaleString()} imported (${pct}%) • ${remaining.toLocaleString()} remaining`,
      percent: pct,
    };
  }
  if (total != null) {
    return {
      tone: "info",
      label: "Analyzing your file",
      detail: `${total.toLocaleString()} items detected`,
      percent: job.stagePct != null ? Math.round(job.stagePct * 100) : null,
    };
  }
  if (processed != null) {
    // Legacy streaming path: total unknown until the end, so count up.
    return { tone: "info", label: "Importing products", detail: `${processed.toLocaleString()} items imported so far…`, percent: null };
  }
  return {
    tone: "info",
    label: "Analyzing your file",
    detail: undefined,
    percent: job.stagePct != null ? Math.round(job.stagePct * 100) : null,
  };
}
